/**
 * expr 的**词法 + 语法 + 静态检查**。
 *
 * 三步合成一个模块，因为它们共享同一份「位置」信息，拆开只会让
 * 报错要来回传偏移量：
 *
 *   ① `tokenize` —— 切词
 *   ② `parseExpression` —— 递归下降成 AST
 *   ③ `validate` —— **静态**白名单检查（未知标识符 / 非法调用）
 *
 * ## 这一版**故意不支持**什么
 *
 * 语句（`if` / `for` / `return`）、变量声明、赋值、箭头函数、`new`、
 * 可选链 `?.`、展开 `...`、`==` / `!=`。
 *
 * 前四个是「这不是表达式语言」的必然结果。后两个是取舍：
 *   · `?.` —— 用 `helpers.get(x, 'a.b')` 代替（也更好读）；
 *   · `==` / `!=` —— **故意不给**。它们的隐式转换是 bug 温床
 *     （`"9" > "10"` 判成真那类）。要比较就写 `a === b`，
 *     或者在类型可能不一致时显式 `num(a) === num(b)`。
 *     报错信息里会直接把这两条路指出来。
 */
import type { BinaryOp, ExprNode, ExprProblem } from './ast'
import { isApiMethod, isApiProp, rootMemberOf } from './whitelist'
import { HELPER_NAMES } from './helpers'

/* ══ ① 切词 ══════════════════════════════════════════════════════ */

type TokenKind = 'num' | 'str' | 'template' | 'ident' | 'punct' | 'eof'

interface Token {
  kind: TokenKind
  /** 字面量 token 带上解析好的值；模板字符串带上交替的片段 */
  value: string
  template?: (string | { source: string })[]
  at: number
}

const PUNCT = [
  '===',
  '!==',
  '<=',
  '>=',
  '&&',
  '||',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  ',',
  '.',
  '?',
  ':',
  '!',
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
] as const

class ExprError extends Error {
  constructor(
    message: string,
    readonly at: number,
  ) {
    super(message)
  }
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = []
  let i = 0

  while (i < source.length) {
    const ch = source[i]!

    if (/\s/.test(ch)) {
      i += 1
      continue
    }

    /* ── 数字 ── */
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(source[i + 1] ?? ''))) {
      const start = i
      while (i < source.length && /[0-9._]/.test(source[i]!)) i += 1
      /* 指数部分 */
      if (/[eE]/.test(source[i] ?? '') && /[0-9+-]/.test(source[i + 1] ?? '')) {
        i += 1
        if (/[+-]/.test(source[i]!)) i += 1
        while (i < source.length && /[0-9]/.test(source[i]!)) i += 1
      }
      const raw = source.slice(start, i)
      const value = Number(raw.replace(/_/g, ''))
      if (Number.isNaN(value)) throw new ExprError(`数字写错了：「${raw}」`, start)
      tokens.push({ kind: 'num', value: String(value), at: start })
      continue
    }

    /* ── 字符串 ── */
    if (ch === '"' || ch === "'") {
      const quote = ch
      const start = i
      i += 1
      let text = ''
      while (i < source.length && source[i] !== quote) {
        if (source[i] === '\\') {
          const esc = source[i + 1]
          text += unescapeChar(esc, source, start, i)
          i += 2
          continue
        }
        text += source[i]
        i += 1
      }
      if (source[i] !== quote) throw new ExprError('字符串没有收尾的引号', start)
      i += 1
      tokens.push({ kind: 'str', value: text, at: start })
      continue
    }

    /* ── 模板字符串 ── */
    if (ch === '`') {
      const start = i
      i += 1
      const parts: (string | { source: string })[] = []
      let text = ''
      while (i < source.length && source[i] !== '`') {
        if (source[i] === '\\') {
          text += unescapeChar(source[i + 1], source, start, i)
          i += 2
          continue
        }
        if (source[i] === '$' && source[i + 1] === '{') {
          parts.push(text)
          text = ''
          i += 2
          const from = i
          let depth = 1
          while (i < source.length && depth > 0) {
            if (source[i] === '{') depth += 1
            else if (source[i] === '}') depth -= 1
            if (depth === 0) break
            i += 1
          }
          if (depth !== 0) throw new ExprError('${…} 里的表达式没有收尾', from)
          parts.push({ source: source.slice(from, i) })
          i += 1
          continue
        }
        text += source[i]
        i += 1
      }
      if (source[i] !== '`') throw new ExprError('模板字符串没有收尾的反引号', start)
      i += 1
      parts.push(text)
      tokens.push({ kind: 'template', value: '', template: parts, at: start })
      continue
    }

    /* ── 标识符 ── */
    if (/[A-Za-z_$]/.test(ch)) {
      const start = i
      while (i < source.length && /[A-Za-z0-9_$]/.test(source[i]!)) i += 1
      tokens.push({ kind: 'ident', value: source.slice(start, i), at: start })
      continue
    }

    /* ── 符号 ── */
    const punct = PUNCT.find((p) => source.startsWith(p, i))
    if (punct) {
      /*
       * `==` / `!=` 单独认出来，好给一条**有指向**的报错
       * （笼统地说「语法错误」等于没说）。
       */
      if (punct === '!' && source.startsWith('!=', i) && !source.startsWith('!==', i)) {
        throw new ExprError(
          '不支持 `!=`。用 `!==`；两边类型可能不一致时用 `num(a) !== num(b)`',
          i,
        )
      }
      tokens.push({ kind: 'punct', value: punct, at: i })
      i += punct.length
      continue
    }
    if (ch === '=' && source[i + 1] === '=') {
      throw new ExprError('不支持 `==`。用 `===`；两边类型可能不一致时用 `num(a) === num(b)`', i)
    }
    if (ch === '=') {
      throw new ExprError('expr 里不能赋值（没有变量）—— 它只算一个值', i)
    }

    throw new ExprError(`不认识的字符「${ch}」`, i)
  }

  tokens.push({ kind: 'eof', value: '', at: source.length })
  return tokens
}

function unescapeChar(esc: string | undefined, source: string, start: number, at: number): string {
  switch (esc) {
    case 'n':
      return '\n'
    case 't':
      return '\t'
    case 'r':
      return '\r'
    case '\\':
      return '\\'
    case '`':
      return '`'
    case "'":
      return "'"
    case '"':
      return '"'
    case '$':
      return '$'
    case 'u': {
      const hex = source.slice(at + 2, at + 6)
      if (!/^[0-9a-fA-F]{4}$/.test(hex)) throw new ExprError('\\u 后面要跟 4 位十六进制', at)
      return String.fromCharCode(Number.parseInt(hex, 16))
    }
    default:
      if (esc === undefined) throw new ExprError('转义符后面没有字符', start)
      return esc
  }
}

/* ══ ② 语法 ══════════════════════════════════════════════════════ */

class Parser {
  private pos = 0

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos]!
  }

  private eat(value: string): boolean {
    if (this.peek().value === value && this.peek().kind === 'punct') {
      this.pos += 1
      return true
    }
    return false
  }

  private expect(value: string): void {
    if (!this.eat(value)) {
      const token = this.peek()
      throw new ExprError(
        `这里应该是 \`${value}\`，实际是 ${token.kind === 'eof' ? '结尾' : `\`${token.value}\``}`,
        token.at,
      )
    }
  }

  parse(): ExprNode {
    const node = this.ternary()
    const token = this.peek()
    if (token.kind !== 'eof') {
      throw new ExprError(`\`${token.value}\` 之后还有多余的东西`, token.at)
    }
    return node
  }

  private ternary(): ExprNode {
    const test = this.binary(0)
    if (!this.eat('?')) return test
    const yes = this.ternary()
    this.expect(':')
    const no = this.ternary()
    return { kind: 'cond', test, yes, no }
  }

  /**
   * 运算符优先级：**下标越小越松**（0 是最外层）。
   *
   * ⚠️ 第一版我写成了「下标越大越松」，而 `binary(level)` 是拿
   * `binary(level + 1)` 当操作数的 —— 两者叠加，优先级**整个反了**：
   * `2 + 3 * 4` 算成 20。测试当场抓住。
   * 表的方向和递归的方向必须一致，这里记一笔。
   */
  private static readonly LEVELS: (readonly BinaryOp[])[] = [
    ['||'],
    ['&&'],
    ['===', '!=='],
    ['<', '<=', '>', '>='],
    ['+', '-'],
    ['*', '/', '%'],
  ]

  private binary(level: number): ExprNode {
    const ops = Parser.LEVELS[level]
    if (!ops) return this.unary()
    let left = this.binary(level + 1)
    for (;;) {
      const token = this.peek()
      if (token.kind !== 'punct' || !(ops as readonly string[]).includes(token.value)) break
      this.pos += 1
      const right = this.binary(level + 1)
      left = { kind: 'binary', op: token.value as BinaryOp, left, right }
    }
    return left
  }

  private unary(): ExprNode {
    const token = this.peek()
    if (token.kind === 'punct' && (token.value === '!' || token.value === '-' || token.value === '+')) {
      this.pos += 1
      return { kind: 'unary', op: token.value as '!' | '-' | '+', operand: this.unary() }
    }
    return this.postfix()
  }

  private postfix(): ExprNode {
    let node = this.primary()
    for (;;) {
      if (this.eat('.')) {
        const token = this.peek()
        if (token.kind !== 'ident') throw new ExprError('`.` 后面要跟属性名', token.at)
        this.pos += 1
        node = { kind: 'member', object: node, property: token.value }
        continue
      }
      if (this.eat('[')) {
        const index = this.ternary()
        this.expect(']')
        node = { kind: 'index', object: node, index }
        continue
      }
      if (this.eat('(')) {
        const args: ExprNode[] = []
        if (!this.eat(')')) {
          for (;;) {
            args.push(this.ternary())
            if (this.eat(',')) continue
            this.expect(')')
            break
          }
        }
        node = { kind: 'call', callee: node, args }
        continue
      }
      return node
    }
  }

  private primary(): ExprNode {
    const token = this.peek()

    if (token.kind === 'num') {
      this.pos += 1
      return { kind: 'lit', value: Number(token.value) }
    }
    if (token.kind === 'str') {
      this.pos += 1
      return { kind: 'lit', value: token.value }
    }
    if (token.kind === 'template') {
      this.pos += 1
      const parts: (string | ExprNode)[] = []
      for (const part of token.template ?? []) {
        if (typeof part === 'string') parts.push(part)
        else parts.push(parseSource(part.source, token.at))
      }
      return { kind: 'template', parts }
    }
    if (token.kind === 'ident') {
      this.pos += 1
      if (token.value === 'true') return { kind: 'lit', value: true }
      if (token.value === 'false') return { kind: 'lit', value: false }
      if (token.value === 'null') return { kind: 'lit', value: null }
      if (token.value === 'undefined') return { kind: 'lit', value: undefined }
      return { kind: 'ref', name: token.value }
    }
    if (this.eat('(')) {
      const inner = this.ternary()
      this.expect(')')
      return inner
    }
    if (this.eat('[')) {
      const items: ExprNode[] = []
      if (!this.eat(']')) {
        for (;;) {
          items.push(this.ternary())
          if (this.eat(',')) {
            /* 允许尾逗号 */
            if (this.eat(']')) break
            continue
          }
          this.expect(']')
          break
        }
      }
      return { kind: 'array', items }
    }
    if (this.eat('{')) {
      const entries: { key: string; value: ExprNode }[] = []
      if (!this.eat('}')) {
        for (;;) {
          const key = this.peek()
          if (key.kind !== 'ident' && key.kind !== 'str') {
            throw new ExprError('对象的键要是名字或字符串', key.at)
          }
          this.pos += 1
          this.expect(':')
          entries.push({ key: key.value, value: this.ternary() })
          if (this.eat(',')) {
            if (this.eat('}')) break
            continue
          }
          this.expect('}')
          break
        }
      }
      return { kind: 'object', entries }
    }

    throw new ExprError(
      token.kind === 'eof' ? '表达式是空的' : `这里不该出现 \`${token.value}\``,
      token.at,
    )
  }
}

function parseSource(source: string, offset: number): ExprNode {
  try {
    return new Parser(tokenize(source)).parse()
  } catch (error) {
    if (error instanceof ExprError) throw new ExprError(error.message, error.at + offset)
    throw error
  }
}

/* ══ ③ 静态白名单 ════════════════════════════════════════════════ */

const ROOTS = new Set(['api', 'helpers'])

/**
 * 原型链逃逸的入口 —— 这些键**编译期能看出来就编译期挡**。
 *
 * ⚠️ 静态检查只能挡**字面量**键名（`x.constructor` / `x["constructor"]`）。
 * 动态键（`x[k]`）静态看不出来，由运行时兜底（`evaluate.ts` 的 `blockedKey`）。
 * 两层都要有：静态的报错更早更准，运行时的是最后一道。
 */
const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype'])

function validate(node: ExprNode, problems: ExprProblem[]): void {
  switch (node.kind) {
    case 'lit':
      return

    case 'ref': {
      /*
       * ⚠️ 第一版这里写成了 `visitChildren(node, problems)` —— 而 `ref` 是叶子，
       * 那个函数对它是空操作，于是**未知标识符一个都没报出来**。
       * 白名单校验漏掉最大的一类错误，等于白名单不存在。
       */
      if (!ROOTS.has(node.name)) {
        problems.push({
          message: `未知标识符「${node.name}」—— expr 里能用的只有 api 和 helpers`,
          at: -1,
        })
      }
      return
    }

    case 'call': {
      const target = rootMemberOf(node.callee)
      if (!target) {
        problems.push({
          message: '只能调用 `api.…` / `helpers.…` 上的方法（这是 expr 的沙箱规则）',
          at: -1,
        })
      } else if (target.root !== 'api' && target.root !== 'helpers') {
        /*
         * ⚠️ 这一条是**补上来的洞**：原来只查了「api 上有没有这个名字」
         * 和「helpers 上有没有这个名字」，**没查根对不对** ——
         * 于是 `Array.isArray(x)` 能**通过编译**（根是 `Array`，两个 if 都不进），
         * 直到运行时才抛「不能调用 Array 上的东西」。
         *
         * 编译期能挡的没挡，等于把沙箱的关键一条降级成了运行时错误 ——
         * 而包作者看到的是「求值失败」，不是「这里不能这么写」。
         */
        problems.push({
          message: `只能调用 api / helpers 上的东西，不能调用 \`${target.root}\``,
          at: -1,
        })
      } else if (target.root === 'api' && !isApiMethod(target.member)) {
        problems.push({ message: `api 上没有可调用的 \`${target.member}\``, at: -1 })
      } else if (target.root === 'helpers' && !HELPER_NAMES.has(target.member)) {
        problems.push({ message: `没有名为 \`${target.member}\` 的 helper`, at: -1 })
      }
      for (const arg of node.args) validate(arg, problems)
      return
    }

    case 'member': {
      /*
       * 只有**根上的直接属性**受名字约束；更深一层是数据访问，
       * 由运行时挡危险键（`__proto__` 等）。
       */
      if (BLOCKED_KEYS.has(node.property)) {
        problems.push({ message: `不许访问 \`${node.property}\``, at: -1 })
        return
      }
      if (node.object.kind === 'ref' && ROOTS.has(node.object.name)) {
        const root = node.object.name
        if (root === 'api' && !isApiMethod(node.property) && !isApiProp(node.property)) {
          problems.push({ message: `api 上没有 \`${node.property}\``, at: -1 })
        }
        if (root === 'helpers' && !HELPER_NAMES.has(node.property)) {
          problems.push({ message: `没有名为 \`${node.property}\` 的 helper`, at: -1 })
        }
        return
      }
      validate(node.object, problems)
      return
    }

    default:
      visitChildren(node, problems)
  }
}

function visitChildren(node: ExprNode, problems: ExprProblem[]): void {
  switch (node.kind) {
    case 'unary':
      validate(node.operand, problems)
      return
    case 'binary':
      validate(node.left, problems)
      validate(node.right, problems)
      return
    case 'cond':
      validate(node.test, problems)
      validate(node.yes, problems)
      validate(node.no, problems)
      return
    case 'array':
      for (const item of node.items) validate(item, problems)
      return
    case 'object':
      for (const entry of node.entries) {
        if (BLOCKED_KEYS.has(entry.key)) {
          problems.push({ message: `不许用键 \`${entry.key}\``, at: -1 })
          continue
        }
        validate(entry.value, problems)
      }
      return
    case 'index': {
      if (node.index.kind === 'lit' && BLOCKED_KEYS.has(String(node.index.value))) {
        problems.push({ message: `不许访问 \`${String(node.index.value)}\``, at: -1 })
        return
      }
      validate(node.object, problems)
      validate(node.index, problems)
      return
    }
    case 'template':
      for (const part of node.parts) if (typeof part !== 'string') validate(part, problems)
      return
    default:
      return
  }
}

/* ══ 对外 ════════════════════════════════════════════════════════ */

export type ParseResult =
  | { ok: true; ast: ExprNode }
  | { ok: false; problems: ExprProblem[] }

/**
 * 把一个表达式源码编译成 AST，并做静态检查。
 *
 * ⚠️ 静态检查**在编译期就把错误挡掉**，而不是等到求值时才抛 ——
 * 一份写错的包不应该等到用户连了线才发现，而且那时报的错
 * （「Cannot read property of undefined」）根本指不到问题在哪。
 */
export function parseExpression(source: string): ParseResult {
  const text = source.trim()
  if (text === '') return { ok: false, problems: [{ message: '表达式是空的', at: 0 }] }

  try {
    const ast = parseSource(text, 0)
    /*
     * ⚠️ 校验失败时**位置信息会丢**（`validate` 走的是 AST，没有偏移量）。
     * 这是当前的取舍：AST 节点上不带 `at` 让整棵树更轻。
     * 报错信息里已经点名了具体是哪个名字，够定位；以后要画波浪线
     * 再给 AST 加位置。
     */
    const problems: ExprProblem[] = []
    validate(ast, problems)
    if (problems.length > 0) return { ok: false, problems }
    return { ok: true, ast }
  } catch (error) {
    if (error instanceof ExprError) {
      return { ok: false, problems: [{ message: error.message, at: error.at }] }
    }
    return {
      ok: false,
      problems: [{ message: `解析失败：${error instanceof Error ? error.message : String(error)}`, at: 0 }],
    }
  }
}
